import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, CameraOff, Maximize2, Minimize2, Settings, Image as ImageIcon, Play, Square, Power, Disc, Grid, X, Volume2, VolumeX, Move, Moon } from 'lucide-react';
import { Toggle } from '../components/ui/FormControls';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WebCodecsPlayer } from '../components/WebCodecsPlayer';
import { MSEPlayer } from '../components/MSEPlayer';
import { PTZControls } from '../components/Cameras/PTZControls';
import { useWallMode, bestColumns } from '../hooks/useWallMode';

const API_BASE = `/api`;

// Touch devices (phones, tablets) → always use the lightweight substream for live.
// Pointer capability is stable for the session (orientation/resize don't change it).
const IS_TOUCH_DEVICE = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;

// Safari / iOS WebKit (NOT Chrome/Firefox/Edge on iOS, which report their own
// token). The A9/2GB iPad (5th gen) runs out of WebKit content-process memory
// when N hardware H.264 decoders + canvas loops run at once and the tab
// silently reloads → mass disconnect. IS_IOS_SAFARI is the constrained target
// that gets the lightweight grid-live path under the 'auto' profile; the
// 'light' / 'full' user profiles override that per user (see liteEligible).
const IS_IOS_WEBKIT = typeof navigator !== 'undefined'
    && /AppleWebKit/.test(navigator.userAgent)
    && !/(Chrome|Chromium|CriOS|FxiOS|EdgiOS|Android)/.test(navigator.userAgent);

const IS_IOS_SAFARI = IS_IOS_WEBKIT && IS_TOUCH_DEVICE;

const VideoPlayer = ({
    camera,
    index,
    onFocus,
    isFocused,
    onToggleActive,
    onToggleRecording,
    isRecording,
    isLiveMotion,
    liveMotionData,
    healthStatus,
    isAuditing,
    onToggleAudio,
    canControl = false,
    isMultiView = false,
}) => {
    const { token, user } = useAuth();
    const { showToast } = useToast();
    const [loadState, setLoadState] = useState('loading');
    const [frameSrc, setFrameSrc] = useState('');
    const navigate = useNavigate();
    const mountedRef = useRef(true);
    const hasFallenBackRef = useRef(false); // prevents duplicate fallback toasts
    const containerRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showPTZ, setShowPTZ] = useState(false);

    // ── Lightweight live path ────────────────────────────────────────────────
    // Every camera stays LIVE, but only the focused / fullscreen tile runs a
    // full H.264 WebSocket decoder — grid tiles refresh via JPEG (fast on
    // screen, slower when scrolled away, paused when the tab is backgrounded)
    // so a weak device never has N hardware decoders + canvas loops open at once.
    //
    // When it kicks in is driven by the current user's `live_view_profile`
    // (set per user by an admin in Settings → Users):
    //   'auto'  (default) → lite path only on iPhone/iPad Safari
    //   'light'           → lite path on every device
    //   'full'            → never (always the full decoder, like desktop)
    const liveProfile = user?.live_view_profile || 'auto';
    const liteEligible = liveProfile === 'light'
        || (liveProfile !== 'full' && IS_IOS_SAFARI);

    const [isTileOnScreen, setIsTileOnScreen] = useState(true);
    const [isPageVisible, setIsPageVisible] = useState(
        typeof document === 'undefined' || document.visibilityState !== 'hidden'
    );
    const safariLiteLive = liteEligible && !isFocused && !isFullscreen;

    useEffect(() => {
        if (!liteEligible) return;
        const el = containerRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;
        const io = new IntersectionObserver(
            ([entry]) => setIsTileOnScreen(entry.isIntersecting),
            { rootMargin: '300px', threshold: 0.01 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [liteEligible]);

    useEffect(() => {
        if (!liteEligible) return;
        const onVis = () => setIsPageVisible(document.visibilityState !== 'hidden');
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [liteEligible]);

    // Night vision (IrCutFilter) — null = not yet fetched, AUTO / ON / OFF
    // ONVIF IrCutFilter:  ON = IR blocked = day/colour  |  OFF = IR allowed = night/B&W
    const [nvMode, setNvMode] = useState(null);
    const [nvLoading, setNvLoading] = useState(false);
    const [nvSupported, setNvSupported] = useState(null); // null = unknown, false = camera lacks IrCutFilter
    const NV_CYCLE = ['AUTO', 'OFF', 'ON']; // AUTO → Night (OFF) → Day (ON) → AUTO

    // Pre-fetch night-vision state once on mount so the badge shows the live day/night
    // mode immediately, and the button hides itself on cameras that lack IrCutFilter.
    // Staggered by tile index so a large grid doesn't fire N heavy ONVIF probes at once.
    useEffect(() => {
        const canSeeNV = camera.onvif_host && (user?.role === 'admin' || canControl);
        if (!token || !canSeeNV) return;
        const controller = new AbortController();
        const delay = Math.min(index, 12) * 300; // spread probes over time, cap the lag
        const timer = setTimeout(() => {
            (async () => {
                try {
                    const res = await fetch(`${API_BASE}/onvif/nightvision/${camera.id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: controller.signal
                    });
                    if (!res.ok) return;
                    const data = await res.json();
                    setNvSupported(data.supported);
                    if (data.mode) setNvMode(data.mode);
                } catch { /* aborted or network error — leave state unknown */ }
            })();
        }, delay);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [camera.id, camera.onvif_host, token, user?.role, canControl, index]);

    const cycleNightVision = async () => {
        if (nvLoading) return;
        setNvLoading(true);
        try {
            let current = nvMode;
            if (current === null) {
                // Discover current state from camera if not already known
                const res = await fetch(`${API_BASE}/onvif/nightvision/${camera.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setNvSupported(data.supported);
                    current = data.mode || 'AUTO';
                } else {
                    current = 'AUTO';
                }
            }
            const next = NV_CYCLE[(NV_CYCLE.indexOf(current) + 1) % NV_CYCLE.length];
            setNvMode(next); // optimistic
            const res = await fetch(`${API_BASE}/onvif/nightvision/${camera.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ mode: next })
            });
            if (!res.ok) {
                setNvMode(current); // revert
                showToast(`${camera.name}: impossible de changer la vision nocturne`, 'error');
            }
        } catch {
            showToast(`${camera.name}: erreur vision nocturne`, 'error');
        } finally {
            setNvLoading(false);
        }
    };


    // 'webcodecs' = primary path (VideoDecoder available)
    // 'mse'       = experimental fMP4/MediaSource pipeline
    // 'fallback'  = JPEG polling (VideoDecoder unavailable or hard fail)
    const [streamMode, setStreamMode] = useState(() => {
        const preferred = camera.live_view_mode || 'auto';
        const hasWebCodecs = 'VideoDecoder' in window;

        if (preferred === 'mjpeg') return 'fallback';
        if (preferred === 'webcodecs') return 'webcodecs';
        if (preferred === 'mse') return 'mse';

        // 'auto' logic
        return hasWebCodecs ? 'webcodecs' : 'fallback';
    });
    // Safari grid tiles are forced onto the JPEG live path regardless of streamMode
    // (see safariLiteLive). The focused / fullscreen tile keeps its real decoder.
    const useWebCodecs = streamMode === 'webcodecs' && !safariLiteLive;
    const useMSE = streamMode === 'mse' && !safariLiteLive;

    // Adaptive live quality (NVR-pro pattern). Server falls back to mainstream
    // automatically when the camera has no substream configured.
    //   touch device            → sub  (iPhone/iPad/Android stability + bandwidth)
    //   desktop fullscreen       → main (HD)
    //   desktop multi-view grid  → sub
    //   desktop single view      → main
    const liveQuality = useMemo(() => {
        if (IS_TOUCH_DEVICE) return 'sub';
        if (isFullscreen) return 'main';
        if (isMultiView) return 'sub';
        return 'main';
    }, [isFullscreen, isMultiView]);

    // JPEG live / fallback polling. Also the Safari grid-tile live path
    // (safariLiteLive → useWebCodecs/useMSE are false here).
    useEffect(() => {
        if (!token || useWebCodecs || useMSE) return; // Skip if using WebCodecs or MSE
        if (liteEligible && !isPageVisible) return;   // tab/app backgrounded → stop polling

        mountedRef.current = true;
        let timeoutId = null;
        let retryCount = 0;

        const fetchFrame = async () => {
            if (!mountedRef.current || useWebCodecs || useMSE) return;

            try {
                const frameUrl = `${API_BASE}/cameras/${camera.id}/frame?t=${Date.now()}`;
                const response = await fetch(frameUrl, {
                    credentials: 'include',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!mountedRef.current || useWebCodecs || useMSE) return;

                if (response.ok) {
                    const blob = await response.blob();
                    if (!mountedRef.current || useWebCodecs || useMSE) return;

                    const url = URL.createObjectURL(blob);
                    setFrameSrc(prev => {
                        if (prev) URL.revokeObjectURL(prev); // Clean up old blob
                        return url;
                    });
                    setLoadState('loaded');
                    retryCount = 0; // Reset on success
                } else if (response.status === 401 || response.status === 403) {
                    setLoadState('unauthorized');
                    retryCount++;
                } else {
                    setLoadState('error');
                    retryCount++;
                }
            } catch (err) {
                console.debug(`Frame fetch error for ${camera.id}`, err);
                setLoadState('error');
                retryCount++;
            } finally {
                if (mountedRef.current && !useWebCodecs && !useMSE) {
                    // Backoff logic: if multiple errors, increase delay to avoid hammering the camera/IP ban
                    // max 10s delay
                    let baseDelay;
                    if (isFocused) baseDelay = 50;
                    else if (safariLiteLive) baseDelay = isTileOnScreen ? 300 : 1200; // Safari grid tile: live but light
                    else baseDelay = 200;
                    const errorDelay = Math.min(retryCount * 2000, 10000);
                    const delay = baseDelay + errorDelay;

                    timeoutId = setTimeout(fetchFrame, delay);
                }
            }
        };

        const startDelay = setTimeout(() => {
            fetchFrame();
        }, index * 150);

        return () => {
            mountedRef.current = false;
            clearTimeout(startDelay);
            if (timeoutId) clearTimeout(timeoutId);

            setFrameSrc(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return '';
            });
        };
    }, [camera.id, token, index, isFocused, useWebCodecs, useMSE, safariLiteLive, liteEligible, isTileOnScreen, isPageVisible]);

    // Memoized with useCallback so its reference is stable across renders.
    // Without this, every parent re-render creates a new function → the useEffect
    // in WebCodecsPlayer (deps: [status, onStateChange]) fires on every render
    // even when status hasn't changed → infinite toast spam loop.
    const handleWebCodecsState = useCallback((status) => {
        if (status === 'unsupported' || status === 'error') {
            // Guard: only show toast + switch mode ONCE per mount cycle.
            // Prevents duplicate toasts from re-render cascade.
            if (hasFallenBackRef.current) return;
            hasFallenBackRef.current = true;

            if (status === 'unsupported') {
                console.warn(`[LiveView] VideoDecoder unsupported for Camera ${camera.id}. Switching to JPEG polling.`);
                if (!window.isSecureContext) {
                    showToast(`${camera.name}: WebCodecs requires HTTPS/localhost. Falling back to MJPEG.`, 'warning');
                } else {
                    showToast(`${camera.name}: WebCodecs unsupported. Falling back to MJPEG.`, 'info');
                }
            } else {
                console.warn(`[LiveView] WebCodecs exhausted retries for Camera ${camera.id}. Switching to JPEG polling.`);
                showToast(`${camera.name}: Stream error. Falling back to MJPEG.`, 'error');
            }
            setStreamMode('fallback');
            setLoadState('loading');
        } else {
            setLoadState(status);
        }
    }, [camera.id, camera.name, showToast]);

    const handleFullscreen = (e) => {
        const el = e?.currentTarget?.closest('.video-container') || containerRef.current;
        if (el?.requestFullscreen) el.requestFullscreen();
    };

    const handleExitFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    };

    // Listen for fullscreen changes — check that THIS container is the fullscreen element,
    // not another camera's container. Without this check, ALL cameras see isFullscreen=true
    // when any one camera goes fullscreen, causing a mass WS reconnection storm.
    useEffect(() => {
        const handleFullscreenChange = () => {
            const el = document.fullscreenElement;
            setIsFullscreen(
                !!el &&
                !!containerRef.current &&
                (el === containerRef.current || containerRef.current.contains(el))
            );
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Derived Display State
    const currentHealth = healthStatus || 'CONNECTED';

    // Prioritize backend health status. If the engine says it's dead, it's dead,
    // even if the frontend still thinks it has a 'loaded' state.
    const isOffline = currentHealth === 'UNREACHABLE' || currentHealth === 'OFFLINE';
    const isUnauthorized = currentHealth === 'UNAUTHORIZED' || loadState === 'unauthorized';

    return (
        <div ref={containerRef} className={`video-container relative w-full bg-black rounded-xl overflow-hidden ${isFullscreen ? 'h-full' : 'aspect-video'} group transition-all duration-300 ${isFocused ? 'ring-4 ring-primary z-30' : 'z-10'}`}>

            {/* INTERNAL BORDER */}
            <div className="absolute inset-0 rounded-xl pointer-events-none z-50 border border-white/10" />

            {/* EXIT FULLSCREEN BUTTON */}
            {isFullscreen && (
                <button
                    onClick={handleExitFullscreen}
                    className="absolute top-4 right-4 z-[60] p-3 bg-black/70 hover:bg-black/90 text-white rounded-full shadow-2xl backdrop-blur-sm transition-all active:scale-90 border border-white/20"
                    title="Exit Fullscreen"
                >
                    <X className="w-6 h-6" />
                </button>
            )}
            {/* TOP LEFT - STATUS & INFO */}
            <div className="absolute top-2 left-2 z-40 flex flex-col gap-1.5 pointer-events-none">

                {/* ERROR BADGES */}
                {isOffline && (
                    <div className="flex items-center space-x-2 bg-zinc-800 px-2.5 py-1 rounded shadow-2xl ring-1 ring-white/20 w-fit">
                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                        <span className="text-[10px] font-black text-white tracking-widest uppercase">NO SIGNAL</span>
                    </div>
                )}
                {isUnauthorized && (
                    <div className="flex items-center space-x-2 bg-amber-600 px-2.5 py-1 rounded shadow-2xl ring-1 ring-white/20 w-fit">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-200" />
                        <span className="text-[10px] font-black text-white tracking-widest uppercase">AUTH ERROR</span>
                    </div>
                )}


                {/* LIGHT-MODE BADGE — always visible (no hover) so it's obvious on
                    iPad that the lightweight live path is active for this tile. */}
                {safariLiteLive && (
                    <div className="flex items-center space-x-1.5 bg-sky-600/90 px-2 py-1 rounded shadow-lg ring-1 ring-white/20 w-fit"
                         title={`Live View léger actif (profil "${liveProfile}") — grille en JPEG, flux H.264 complet en plein écran`}>
                        <div className="w-1.5 h-1.5 rounded-full bg-sky-100 animate-pulse" />
                        <span className="text-[10px] font-black text-white tracking-widest uppercase">Léger</span>
                    </div>
                )}

                {/* Camera Information Card */}
                {!showPTZ && (
                    <div className="bg-black/60 backdrop-blur-md px-2.5 py-2 rounded-lg border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-fit max-w-[200px]">
                        <h3 className="text-white font-bold text-xs sm:text-sm tracking-tight leading-tight truncate">
                            {camera.name}
                        </h3>
                        <div className="text-white/50 text-[9px] mt-0.5 font-mono">
                            {camera.resolution_width}x{camera.resolution_height}
                        </div>
                        {safariLiteLive ? (
                            <div className="text-sky-400/90 text-[9px] mt-0.5 font-mono">
                                LIVE · JPEG (léger · {liveProfile})
                            </div>
                        ) : streamMode === 'webcodecs' ? (
                            <div className="text-green-400/80 text-[9px] mt-0.5 font-mono">
                                WS / H.264
                            </div>
                        ) : streamMode === 'mse' ? (
                            <div className="text-purple-400/80 text-[9px] mt-0.5 font-mono">
                                MSE / fMP4
                            </div>
                        ) : streamMode === 'fallback' ? (
                            <div className="text-yellow-400/70 text-[9px] mt-0.5 font-mono">
                                JPEG Poll
                            </div>
                        ) : null}
                    </div>
                )}
            </div>

            {/* ACTION BAR */}
            {!showPTZ && (
                <div className="absolute inset-x-0 bottom-0 p-2 z-40 opacity-0 group-hover:opacity-100 transition-all duration-300 flex justify-center pointer-events-none">
                    <div className="flex items-center gap-0.5 sm:gap-1 bg-black/80 backdrop-blur-xl p-1 rounded-xl border border-white/10 shadow-3xl pointer-events-auto max-w-full overflow-hidden">
                        <button onClick={() => onFocus(camera.id)} className={`p-1 rounded-md text-white transition-all shrink-0 ${isFocused ? 'bg-primary' : 'hover:bg-white/10'}`} title="Focus">
                            <Square className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <button onClick={handleFullscreen} className="p-1 text-white hover:bg-white/10 rounded-md transition-all shrink-0" title="Fullscreen">
                            <Maximize2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>

                        <div className="w-px h-4 bg-white/10 mx-0.5 self-center shrink-0" />

                        <button onClick={() => {
                            fetch(`${API_BASE}/cameras/${camera.id}/snapshot`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` }
                            }).then(res => { if (res.ok) showToast(`Snapshot saved`, 'success'); });
                        }} className="p-1 text-white hover:bg-white/10 rounded-md transition-all shrink-0" title="Take Photo">
                            <Camera className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <button onClick={() => navigate(`/timeline?camera=${camera.id}&type=snapshot`)} className="p-1 text-white hover:bg-white/10 rounded-md transition-all shrink-0" title="Gallery">
                            <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <button onClick={() => navigate(`/timeline?camera=${camera.id}&type=video`)} className="p-1 text-white hover:bg-white/10 rounded-md transition-all shrink-0" title="Videos">
                            <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>

                        <div className="w-px h-4 bg-white/10 mx-0.5 self-center shrink-0" />

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleRecording(camera);
                            }}
                            className={`p-1 rounded-md transition-all shrink-0 ${(camera.recording_mode === 'Always' || camera.recording_mode === 'Continuous') ? 'bg-red-600 text-white animate-pulse shadow-[0_0_10px_rgba(220,38,38,0.5)]' : 'text-white hover:bg-red-600/50'}`}
                            title={(camera.recording_mode === 'Always' || camera.recording_mode === 'Continuous') ? "Stop Recording" : "Start Continuous Recording"}
                        >
                            <Disc className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>

                        {/* NIGHT VISION — only for ONVIF cameras that expose IrCutFilter */}
                        {camera.onvif_host && (user?.role === 'admin' || canControl) && nvSupported !== false && (
                            <button
                                onClick={(e) => { e.stopPropagation(); cycleNightVision(); }}
                                disabled={nvLoading}
                                title={
                                    nvMode === 'OFF' ? 'Vision nocturne : NUIT (cliquer pour changer)'
                                    : nvMode === 'ON'  ? 'Vision nocturne : JOUR (cliquer pour changer)'
                                    : nvMode === 'AUTO' ? 'Vision nocturne : AUTO (cliquer pour changer)'
                                    : 'Vision nocturne (cliquer pour activer)'
                                }
                                className={`relative p-1 rounded-md transition-all shrink-0 ${nvLoading ? 'opacity-40 cursor-wait' : ''} ${
                                    nvMode === 'OFF'
                                        ? 'bg-blue-600/40 text-blue-300 ring-1 ring-blue-500/60 shadow-[0_0_6px_rgba(37,99,235,0.4)]'
                                        : nvMode === 'ON'
                                            ? 'text-amber-300 hover:bg-white/10'
                                            : 'text-white/50 hover:bg-white/10'
                                }`}
                            >
                                <Moon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                {/* State badge — A(uto) · N(ight) · D(ay) */}
                                <span className={`absolute -top-0.5 -right-0.5 text-[6px] font-black leading-none px-[2px] py-[1px] rounded-sm ${
                                    nvMode === 'OFF'  ? 'bg-blue-500 text-white'
                                    : nvMode === 'ON' ? 'bg-amber-500 text-white'
                                    : nvMode === 'AUTO' ? 'bg-white/25 text-white'
                                    : 'hidden'
                                }`}>
                                    {nvMode === 'OFF' ? 'N' : nvMode === 'ON' ? 'D' : 'A'}
                                </span>
                            </button>
                        )}

                        {(user?.role === 'admin' || canControl) && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowPTZ(!showPTZ);
                                }}
                                className={`p-1 rounded-md transition-all shrink-0 ${showPTZ ? 'bg-primary text-white shadow-[0_0_10px_rgba(var(--primary),0.5)]' : 'text-white hover:bg-primary/50'}`}
                                title={showPTZ ? "Hide PTZ Controls" : "Show PTZ Controls"}
                            >
                                <Move className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                        )}
                        {user?.role === 'admin' && (
                            <button onClick={() => navigate(`/cameras?edit=${camera.id}`)} className="p-1 text-primary-foreground bg-primary hover:bg-primary/80 rounded-md transition-all shrink-0" title="Settings">
                                <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Video Content Layer */}
            {isUnauthorized ? (
                <img src="/invalid-password.png" alt="Invalid Password" className="absolute inset-0 w-full h-full object-cover" />
            ) : isOffline ? (
                <img src="/no-signal.png" alt="No Signal" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
                <>
                    {useMSE && (
                        <MSEPlayer
                            camera={camera}
                            onStateChange={(state) => {
                                if (state === 'error') {
                                    console.warn(`[LiveView] MSE exhausted retries for camera ${camera.id}. Falling back to JPEG.`);
                                    showToast(`${camera.name}: MSE stream failed. Falling back to MJPEG.`, 'warning');
                                    setStreamMode('fallback');
                                    setLoadState('loading');
                                }
                            }}
                        />
                    )}

                    {!useMSE && (useWebCodecs || (camera.audio_enabled && (!liteEligible || isAuditing))) && (
                        <WebCodecsPlayer
                            camera={camera}
                            onStateChange={handleWebCodecsState}
                            videoEnabled={useWebCodecs}
                            isAuditing={isAuditing}
                            quality={liveQuality}
                        />
                    )}

                    {(!useWebCodecs && !useMSE && frameSrc) && (
                        <img
                            src={frameSrc}
                            alt={camera.name}
                            className={`absolute inset-0 w-full h-full object-contain`}
                        />
                    )}

                    {loadState === 'loading' && !useMSE && (
                        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/90">
                            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}

                    {showPTZ && (
                        <PTZControls 
                            camera={camera} 
                            onClose={() => setShowPTZ(false)} 
                            isAuditing={isAuditing}
                            onToggleAudio={onToggleAudio}
                            isWebCodecPlayback={useWebCodecs}
                        />
                    )}
                </>
            )}
        </div>
    );
};


export const LiveView = () => {
    const { t } = useTranslation();
    const { token, user } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [cameras, setCameras] = useState([]);
    // Single state object — 3 fields updated together so only 1 re-render per poll
    const [motionStatus, setMotionStatus] = useState({ activeMotionIds: [], liveMotion: {}, cameraHealth: {} });
    const { activeMotionIds, liveMotion, cameraHealth } = motionStatus;
    const [focusCameraId, setFocusCameraId] = useState(null);
    const [auditingCameraId, setAuditingCameraId] = useState(null);
    const [userPermissions, setUserPermissions] = useState({});
    const [columnSetting, setColumnSetting] = useState(() => {
        return localStorage.getItem('liveViewColumns') || 'auto';
    });

    const fetchCameras = () => {
        fetch(`${API_BASE}/cameras`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => setCameras(prev => {
                // Sort by id to guarantee a stable order across polls
                const sorted = [...data].sort((a, b) => a.id - b.id);
                // Skip re-render if nothing changed
                if (prev.length === sorted.length && prev.every((c, i) => c.id === sorted[i].id && c.is_active === sorted[i].is_active && c.recording_mode === sorted[i].recording_mode)) {
                    return prev;
                }
                return sorted;
            }))
            .catch(err => console.error(err));
    };

    const fetchMotionStatus = () => {
        fetch(`${API_BASE}/events/status`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => {
                setMotionStatus({
                    activeMotionIds: data.active_ids || [],
                    liveMotion: data.live_motion || {},
                    cameraHealth: data.camera_health || {}
                });
            })
            .catch(err => console.error("Failed to fetch motion status", err));
    };

    useEffect(() => {
        if (!token || !user || user.role === 'admin') return;
        fetch(`${API_BASE}/users/${user.id}/camera-permissions`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => {
                if (!Array.isArray(data)) return;
                const map = {};
                data.forEach(p => { map[p.camera_id] = p; });
                setUserPermissions(map);
            })
            .catch(err => console.error('Failed to fetch camera permissions', err));
    }, [token, user]);

    useEffect(() => {
        if (!token) return;

        fetchCameras();
        fetchMotionStatus();

        // Poll for motion status more frequently
        const statusInterval = setInterval(fetchMotionStatus, 3000);
        // Refresh cameras list less frequently
        const cameraInterval = setInterval(fetchCameras, 30000);

        return () => {
            clearInterval(statusInterval);
            clearInterval(cameraInterval);
        };
    }, [token]);

    const toggleFocus = (id) => {
        setFocusCameraId(prev => prev === id ? null : id);
    };

    const handleToggleActive = async (camera) => {
        try {
            const res = await fetch(`${API_BASE}/cameras/${camera.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ ...camera, is_active: !camera.is_active })
            });
            if (res.ok) fetchCameras();
        } catch (err) { console.error(err); }
    };

    const handleToggleRecording = async (camera) => {
        try {
            const res = await fetch(`${API_BASE}/cameras/${camera.id}/recording`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (res.ok) fetchCameras();
        } catch (err) { console.error(err); }
    };

    const handleToggleAudio = (id) => {
        setAuditingCameraId(prev => prev === id ? null : id);
    };

    const motionFirstKey = `liveViewMotionFirst_${user?.id ?? 'anon'}`;
    const [motionFirst, setMotionFirst] = useState(() => localStorage.getItem(motionFirstKey) === 'true');
    const handleMotionFirstToggle = (val) => {
        setMotionFirst(val);
        localStorage.setItem(motionFirstKey, val);
    };
    const { isWall, enter: enterWall, exit: exitWall, viewport } = useWallMode();

    const [selectedGroup, setSelectedGroup] = useState('all');
    const [isGroupView, setIsGroupView] = useState(() => {
        return localStorage.getItem('liveViewGroupBy') === 'true';
    });

    const handleGroupViewToggle = (val) => {
        setIsGroupView(val);
        localStorage.setItem('liveViewGroupBy', val);
    };

    const activeCameras = useMemo(() => cameras.filter(c => c.is_active), [cameras]);

    const availableGroups = useMemo(
        () => [...new Set(cameras.flatMap(c => c.groups ? c.groups.map(g => g.name) : []))].sort(),
        [cameras]
    );

    const groupFilteredCameras = useMemo(
        () => selectedGroup === 'all'
            ? activeCameras
            : activeCameras.filter(c => c.groups && c.groups.some(g => g.name === selectedGroup)),
        [activeCameras, selectedGroup]
    );

    // Optional: cameras with live motion float to the front, most recently triggered first.
    const orderedCameras = useMemo(() => {
        if (!motionFirst) return groupFilteredCameras;
        return [...groupFilteredCameras].sort((a, b) => {
            const aTs = liveMotion[a.id]?.timestamp;
            const bTs = liveMotion[b.id]?.timestamp;
            if (aTs && bTs) return new Date(bTs) - new Date(aTs);
            if (aTs) return -1;
            if (bTs) return 1;
            return 0;
        });
    }, [groupFilteredCameras, liveMotion, motionFirst]);

    const displayCameras = useMemo(
        () => focusCameraId
            ? orderedCameras.filter(c => c.id === focusCameraId)
            : orderedCameras,
        [orderedCameras, focusCameraId]
    );

    // Multi-view = a mosaic of >1 tile (no single camera focused). Drives substream usage.
    const isMultiView = !focusCameraId && displayCameras.length > 1;

    // Grid class for group sections (same for every section header)
    const groupGridClass = useMemo(() => {
        const cols = { '1': 'grid-cols-1', '2': 'grid-cols-1 sm:grid-cols-2', '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' };
        return cols[columnSetting] ?? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    }, [columnSetting]);

    // Grid class for the flat view (auto mode considers camera count)
    const flatGridClass = useMemo(() => {
        if (focusCameraId) return 'grid-cols-1';
        if (columnSetting === 'auto') {
            if (cameras.length <= 1) return 'grid-cols-1';
            if (cameras.length <= 4) return 'grid-cols-1 sm:grid-cols-2';
            return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
        }
        const cols = { '1': 'grid-cols-1', '2': 'grid-cols-1 sm:grid-cols-2', '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' };
        return cols[columnSetting] ?? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    }, [columnSetting, cameras.length, focusCameraId]);

    if (isWall) {
        const n = orderedCameras.length;
        const cols = columnSetting === 'auto'
            ? bestColumns(n, viewport.w, viewport.h)
            : Math.min(Number(columnSetting) || 1, Math.max(n, 1));
        const rows = Math.max(Math.ceil(n / cols), 1);
        const gap = 4;
        const tileW = Math.floor(Math.min(
            (viewport.w - gap * (cols - 1)) / cols,
            ((viewport.h - gap * (rows - 1)) / rows) * (16 / 9)
        ));
        return (
            <div className="fixed inset-0 z-[90] bg-black flex items-center justify-center overflow-hidden">
                <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, ${tileW}px)`, gap }}>
                    {orderedCameras.map((cam, i) => (
                        <div key={cam.id} style={{ width: tileW }}>
                            <VideoPlayer
                                index={i}
                                camera={cam}
                                onFocus={() => {}}
                                isFocused={false}
                                onToggleActive={handleToggleActive}
                                onToggleRecording={handleToggleRecording}
                                isRecording={activeMotionIds.includes(cam.id)}
                                isLiveMotion={!!liveMotion[cam.id]}
                                liveMotionData={liveMotion[cam.id]}
                                healthStatus={cameraHealth[String(cam.id)]}
                                isAuditing={auditingCameraId === cam.id}
                                onToggleAudio={handleToggleAudio}
                                canControl={userPermissions[cam.id]?.can_control ?? false}
                                isMultiView={n > 1}
                            />
                        </div>
                    ))}
                </div>
                <div className="absolute top-0 right-0 z-[95] p-3 flex items-center gap-3 opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-black/80 text-white rounded-lg px-3 py-1.5 border border-white/20">
                        <Toggle
                            checked={motionFirst}
                            onChange={handleMotionFirstToggle}
                            label={t('live.motionFirst')}
                            help={t('live.motionFirstHelp')}
                        />
                    </div>
                    <button
                        onClick={exitWall}
                        className="bg-black/80 hover:bg-black text-white rounded-lg px-3 py-2 border border-white/20 flex items-center gap-2 text-sm"
                    >
                        <X className="w-4 h-4" /> {t('live.exit')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col px-5 py-4 lg:p-8">
            <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight flex items-baseline gap-2">
                        Live View
                        <span className="text-lg font-normal text-muted-foreground">({cameras.length} cameras)</span>
                    </h2>
                    <p className="text-muted-foreground mt-2">Monitor your cameras in real-time.</p>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">

                    <div className="flex items-center gap-2">
                        {/* Group Selector */}
                        {availableGroups.length > 0 && (
                            <div className="flex items-center space-x-1 bg-card border border-border rounded-lg p-1">
                                <span className="text-xs text-muted-foreground ml-2 font-medium">Group:</span>
                                <select
                                    className="bg-transparent text-sm border-none focus:ring-0 cursor-pointer py-1 pr-8 pl-1 max-w-[100px] sm:max-w-none"
                                    value={selectedGroup}
                                    onChange={(e) => setSelectedGroup(e.target.value)}
                                >
                                    <option value="all">All</option>
                                    {availableGroups.map(g => (
                                        <option key={g} value={g}>{g}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Group/List Toggle */}
                        <div className="flex items-center px-2">
                            <Toggle
                                checked={isGroupView}
                                onChange={handleGroupViewToggle}
                                label="Group View"
                                help="Group cameras by section"
                            />
                        </div>

                        <div className="flex items-center px-2">
                            <Toggle
                                checked={motionFirst}
                                onChange={handleMotionFirstToggle}
                                label={t('live.motionFirst')}
                                help={t('live.motionFirstHelp')}
                            />
                        </div>
                    </div>

                    {/* Column Selector - Hidden on mobile as it defaults to 1 col anyway */}
                    <div className="hidden sm:flex items-center space-x-1 bg-card border border-border rounded-lg p-1">
                        <Grid className="w-4 h-4 text-muted-foreground ml-2" />
                        <select
                            className="bg-transparent text-sm border-none focus:ring-0 cursor-pointer py-1 pr-8 pl-2"
                            value={columnSetting}
                            onChange={(e) => {
                                const val = e.target.value;
                                setColumnSetting(val);
                                localStorage.setItem('liveViewColumns', val);
                            }}
                        >
                            <option value="auto">Auto</option>
                            <option value="1">1 Col</option>
                            <option value="2">2 Cols</option>
                            <option value="3">3 Cols</option>
                            <option value="4">4 Cols</option>
                        </select>
                    </div>

                    <button
                        onClick={enterWall}
                        disabled={groupFilteredCameras.length === 0}
                        title={t('live.wallTitle')}
                        className="flex items-center gap-1.5 bg-card border border-border rounded-lg px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
                    >
                        <Maximize2 className="w-4 h-4" /> {t('live.wall')}
                    </button>

                    {focusCameraId && (
                        <button onClick={() => setFocusCameraId(null)} className="text-sm text-primary hover:underline">
                            Show All
                        </button>
                    )}
                </div>
            </div>

            {/* Group View Render Logic */}
            {
                isGroupView && selectedGroup === 'all' && !focusCameraId ? (
                    <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                        {/* Render Grouped Sections */}
                        {(() => {
                            // 1. Group cameras by their primary group (or first group found)
                            //    Cameras with no group go to "Ungrouped"
                            //    Cameras with multiple groups: we can duplicate them or just pick the first.
                            //    Let's pick the first group name for sorting.
                            const grouped = {};
                            const ungrouped = [];

                            displayCameras.forEach(cam => {
                                if (cam.groups && cam.groups.length > 0) {
                                    cam.groups.forEach(g => {
                                        if (!grouped[g.name]) grouped[g.name] = [];
                                        // Prevent duplicates in same group if data is weird, 
                                        // but allow camera in multiple DIFFERENT groups.
                                        if (!grouped[g.name].find(c => c.id === cam.id)) {
                                            grouped[g.name].push(cam);
                                        }
                                    });
                                } else {
                                    ungrouped.push(cam);
                                }
                            });

                            const sortedGroupNames = Object.keys(grouped).sort();

                            return (
                                <>
                                    {sortedGroupNames.map(groupName => (
                                        <div key={groupName}>
                                            <h3 className="text-xl font-bold mb-4 text-foreground/90 flex items-center border-b pb-2 sticky top-0 bg-background/95 backdrop-blur z-20 pt-2">
                                                <span className="bg-primary/10 text-primary px-2.5 py-0.5 rounded-md text-sm mr-3 font-mono">
                                                    {grouped[groupName].length}
                                                </span>
                                                {groupName}
                                            </h3>
                                            <div className={`grid gap-2 sm:gap-4 ${groupGridClass}`}>
                                                {grouped[groupName].map((cam, i) => (
                                                    <VideoPlayer
                                                        key={cam.id}
                                                        index={i}
                                                        camera={cam}
                                                        onFocus={toggleFocus}
                                                        isFocused={focusCameraId === cam.id}
                                                        onToggleActive={handleToggleActive}
                                                        onToggleRecording={handleToggleRecording}
                                                        isRecording={activeMotionIds.includes(cam.id)}
                                                        isLiveMotion={!!liveMotion[cam.id]}
                                                        liveMotionData={liveMotion[cam.id]}

                                                        healthStatus={cameraHealth[String(cam.id)]}
                                                        isAuditing={auditingCameraId === cam.id}
                                                        onToggleAudio={handleToggleAudio}
                                                        canControl={userPermissions[cam.id]?.can_control ?? false}
                                                        isMultiView={isMultiView}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    {ungrouped.length > 0 && (
                                        <div>
                                            {sortedGroupNames.length > 0 && (
                                                <h3 className="text-xl font-bold mb-4 text-foreground/90 flex items-center border-b pb-2 sticky top-0 bg-background/95 backdrop-blur z-20 pt-2">
                                                    <span className="bg-primary/10 text-primary px-2.5 py-0.5 rounded-md text-sm mr-3 font-mono">
                                                        {ungrouped.length}
                                                    </span>
                                                    Ungrouped
                                                </h3>
                                            )}
                                            <div className={`grid gap-2 sm:gap-4 ${groupGridClass}`}>
                                                {ungrouped.map((cam, i) => (
                                                    <VideoPlayer
                                                        key={cam.id}
                                                        index={i}
                                                        camera={cam}
                                                        onFocus={toggleFocus}
                                                        isFocused={focusCameraId === cam.id}
                                                        onToggleActive={handleToggleActive}
                                                        onToggleRecording={handleToggleRecording}
                                                        isRecording={activeMotionIds.includes(cam.id)}
                                                        isLiveMotion={!!liveMotion[cam.id]}
                                                        liveMotionData={liveMotion[cam.id]}

                                                        healthStatus={cameraHealth[String(cam.id)]}
                                                        isAuditing={auditingCameraId === cam.id}
                                                        onToggleAudio={handleToggleAudio}
                                                        canControl={userPermissions[cam.id]?.can_control ?? false}
                                                        isMultiView={isMultiView}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                ) : (
                    <div className={`grid gap-2 sm:gap-4 flex-1 min-h-0 pr-2 ${flatGridClass}`}>
                        {displayCameras.map((cam, i) => (
                            <VideoPlayer
                                key={cam.id}
                                index={i}
                                camera={cam}
                                onFocus={toggleFocus}
                                isFocused={focusCameraId === cam.id}
                                onToggleActive={handleToggleActive}
                                onToggleRecording={handleToggleRecording}
                                isRecording={activeMotionIds.includes(cam.id)}
                                isLiveMotion={!!liveMotion[cam.id]}
                                liveMotionData={liveMotion[cam.id]}
                                healthStatus={cameraHealth[String(cam.id)]}
                                isAuditing={auditingCameraId === cam.id}
                                onToggleAudio={handleToggleAudio}
                                canControl={userPermissions[cam.id]?.can_control ?? false}
                                isMultiView={isMultiView}
                            />
                        ))}
                    </div>
                )
            }

            {
                cameras.length === 0 && (
                    <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-xl">
                        <p>No cameras configured.</p>
                    </div>
                )
            }
        </div >
    );
};
